/* ════════════════════════════════════════════════════════════════════
   ROBOT SETTINGS — tab admin (2026-09-24, PLAN_ROBOT_DARI_SERVER.md bahagian B)
   ════════════════════════════════════════════════════════════════════
   Admin mengubah nilai yang extension baca untuk robot TikTok / Facebook Reels / Google Flow (alamat laman, masa
   tunggu, cubaan, peta module, teks yang pelanggan nampak) TANPA ZIP baharu (keputusan user 24 Sept: "a").

   Doc: config/robot_tetapan { nilai: { kunci: nilai }, updatedAt, updatedBy } — HANYA nilai yang BERBEZA daripada lalai
   disimpan (Reset = kunci dibuang). Rules: baca = semua log masuk (extension), tulis = admin.

   ⛔ Semakan di sini = KEMBAR semakan extension (utils/tetapanRobotSkema.js ↔ extension tetapanRobot.ts). Extension
      tetap menyemak semula — nilai yang lolos di sini tetapi gagal di sana dipakai sebagai LALAI (console.error).
   ⚠️ Berkesan HANYA pada extension yang ada pembaca tetapan (ZIP selepas 3.9.4). Extension lama = nilai terbina.
   UI admin = English (peraturan projek).
   ════════════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { auth, db } from '../firebase';
import { KUMPULAN, SKEMA, pemegangWajib, paparNombor, samaNilai, semakNilai } from '../utils/tetapanRobotSkema';

import '../design/tokens.css';
import '../design/ui.css';
import './RobotTetapanTab.css';

const REF = () => doc(db, 'config', 'robot_tetapan');

// Nilai → teks dalam kotak input.
function keInput(sk, v) {
    if (sk.jenis === 'nombor') return sk.unit === 'ms' ? String(+(v / 1000).toFixed(3)) : String(v);
    if (sk.jenis === 'senarai') return (v || []).join('\n');
    if (sk.jenis === 'modul') return JSON.stringify(v, null, 2);
    return String(v ?? '');
}
// Teks dalam kotak input → nilai (atau { ralat }).
function dariInput(sk, s) {
    if (sk.jenis === 'nombor') {
        if (!String(s).trim()) return { ralat: 'Cannot be empty' };
        const x = Number(s);
        if (!Number.isFinite(x)) return { ralat: 'Must be a number' };
        return { nilai: sk.unit === 'ms' ? Math.round(x * 1000) : Math.round(x) };
    }
    if (sk.jenis === 'senarai') return { nilai: String(s).split('\n').map((x) => x.trim()).filter(Boolean) };
    if (sk.jenis === 'modul') {
        try { return { nilai: JSON.parse(s) }; } catch { return { ralat: 'Not valid JSON' }; }
    }
    return { nilai: String(s) };
}

export default function RobotTetapanTab() {
    const [server, setServer] = useState(null);      // { nilai, updatedAt, updatedBy } | null (belum dimuat)
    const [input, setInput] = useState({});           // kunci → teks kotak
    const [memuat, setMemuat] = useState(true);
    const [menyimpan, setMenyimpan] = useState(false);
    const [mesej, setMesej] = useState(null);         // { jenis: 'ok'|'ralat', teks }

    const nilaiSemasa = useCallback((sk, simpan) => {
        const n = simpan?.nilai || {};
        return Object.prototype.hasOwnProperty.call(n, sk.kunci) ? n[sk.kunci] : sk.lalai;
    }, []);

    const muat = useCallback(async () => {
        setMemuat(true); setMesej(null);
        try {
            const snap = await getDoc(REF());
            const data = snap.exists() ? snap.data() : { nilai: {} };
            setServer(data);
            setInput(Object.fromEntries(SKEMA.map((sk) => [sk.kunci, keInput(sk, nilaiSemasa(sk, data))])));
        } catch (e) {
            setMesej({ jenis: 'ralat', teks: 'Could not load settings: ' + (e?.message || e) });
        } finally { setMemuat(false); }
    }, [nilaiSemasa]);

    useEffect(() => { muat(); }, [muat]);

    // Keadaan setiap baris: nilai dihurai, ralat, berbeza daripada tersimpan, berbeza daripada lalai.
    const baris = useMemo(() => {
        const keluar = {};
        for (const sk of SKEMA) {
            const h = dariInput(sk, input[sk.kunci] ?? '');
            const ralat = h.ralat || semakNilai(sk, h.nilai);
            const tersimpan = nilaiSemasa(sk, server);
            keluar[sk.kunci] = {
                nilai: h.nilai, ralat,
                diubah: !ralat && !samaNilai(h.nilai, tersimpan),
                bukanLalai: !ralat ? !samaNilai(h.nilai, sk.lalai) : !samaNilai(tersimpan, sk.lalai),
            };
        }
        return keluar;
    }, [input, server, nilaiSemasa]);

    const bilDiubah = Object.values(baris).filter((b) => b.diubah).length;
    const bilRalat = Object.values(baris).filter((b) => b.ralat).length;
    const bilBukanLalai = Object.values(baris).filter((b) => b.bukanLalai).length;

    const simpan = async () => {
        if (bilRalat) { setMesej({ jenis: 'ralat', teks: `Fix ${bilRalat} invalid value(s) first.` }); return; }
        setMenyimpan(true); setMesej(null);
        try {
            const nilai = {};
            for (const sk of SKEMA) {
                const b = baris[sk.kunci];
                if (!samaNilai(b.nilai, sk.lalai)) nilai[sk.kunci] = b.nilai;   // lalai TIDAK disimpan
            }
            await setDoc(REF(), { nilai, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || auth.currentUser?.uid || '?' });
            await muat();
            setMesej({ jenis: 'ok', teks: 'Saved. Extensions with the settings reader pick this up within seconds.' });
        } catch (e) {
            setMesej({ jenis: 'ralat', teks: 'Save failed: ' + (e?.message || e) });
        } finally { setMenyimpan(false); }
    };

    const ubah = (kunci, v) => setInput((p) => ({ ...p, [kunci]: v }));
    const reset = (sk) => ubah(sk.kunci, keInput(sk, sk.lalai));
    const buangSemua = () => setInput(Object.fromEntries(SKEMA.map((sk) => [sk.kunci, keInput(sk, nilaiSemasa(sk, server))])));

    if (memuat && !server) {
        return <div className="rt-tengah"><Loader2 size={18} className="rt-pusing" /> <span>Loading robot settings…</span></div>;
    }

    const masaSimpan = server?.updatedAt?.toDate ? server.updatedAt.toDate().toLocaleString('en-GB') : '';

    return (
        <div className="rt">
            <div className="rt-kepala">
                <div>
                    <h2 className="rt-h2">Robot settings</h2>
                    <p className="rt-dim">
                        Values the extension reads for the TikTok, Facebook Reels and Google Flow robots. Changes apply within
                        seconds, without a new extension — but only on extensions released after v3.9.4. Older extensions keep the
                        built-in defaults.
                    </p>
                    <p className="rt-dim rt-kecil">
                        {bilBukanLalai ? `${bilBukanLalai} setting(s) differ from default` : 'All settings are at default'}
                        {masaSimpan ? ` · last saved ${masaSimpan} by ${server?.updatedBy || '?'}` : ''}
                    </p>
                </div>
                <button type="button" className="rt-btn rt-btn--ghost" onClick={muat} disabled={memuat || menyimpan}>
                    <RefreshCw size={14} /> Reload
                </button>
            </div>

            {mesej && <div className={`rt-mesej rt-mesej--${mesej.jenis}`}>{mesej.teks}</div>}

            {KUMPULAN.map((kum) => {
                const ahli = SKEMA.filter((sk) => sk.kumpulan === kum.id);
                if (!ahli.length) return null;
                return (
                    <section key={kum.id} className="rt-kumpulan">
                        <h3 className="rt-h3">{kum.label}</h3>
                        {kum.nota && <p className="rt-dim rt-kecil">{kum.nota}</p>}
                        <div className="rt-senarai">
                            {ahli.map((sk) => {
                                const b = baris[sk.kunci];
                                const wajib = pemegangWajib(sk);
                                const besar = sk.jenis === 'modul' || sk.jenis === 'senarai' || (sk.jenis === 'teks' && sk.max > 200);
                                return (
                                    <div key={sk.kunci} className={`rt-baris${b.diubah ? ' rt-baris--diubah' : ''}${b.ralat ? ' rt-baris--ralat' : ''}`}>
                                        <div className="rt-label">
                                            <div className="rt-nama">{sk.label}</div>
                                            <div className="rt-kunci">{sk.kunci}</div>
                                            <div className="rt-lalai">
                                                Default: {sk.jenis === 'nombor' ? paparNombor(sk, sk.lalai)
                                                    : sk.jenis === 'modul' ? 'built-in map (5 modules)'
                                                        : sk.jenis === 'senarai' ? `${sk.lalai.length} lines`
                                                            : <span className="rt-lalai-teks">{String(sk.lalai).replace(/\n/g, ' ⏎ ')}</span>}
                                            </div>
                                            {wajib.length > 0 && <div className="rt-lalai">Keep: {wajib.join(' ')}</div>}
                                        </div>
                                        <div className="rt-input">
                                            {besar ? (
                                                <textarea
                                                    className="rt-medan rt-medan--besar"
                                                    rows={sk.jenis === 'modul' ? 14 : Math.min(8, Math.max(3, String(input[sk.kunci] || '').split('\n').length + 1))}
                                                    value={input[sk.kunci] ?? ''}
                                                    onChange={(e) => ubah(sk.kunci, e.target.value)}
                                                    spellCheck={false}
                                                />
                                            ) : sk.jenis === 'teks' ? (
                                                <textarea
                                                    className="rt-medan"
                                                    rows={Math.max(1, String(input[sk.kunci] || '').split('\n').length)}
                                                    value={input[sk.kunci] ?? ''}
                                                    onChange={(e) => ubah(sk.kunci, e.target.value)}
                                                />
                                            ) : (
                                                <div className="rt-nombor">
                                                    <input
                                                        className="rt-medan"
                                                        type={sk.jenis === 'nombor' ? 'number' : 'text'}
                                                        step={sk.jenis === 'nombor' && sk.unit === 'ms' ? '0.1' : '1'}
                                                        value={input[sk.kunci] ?? ''}
                                                        onChange={(e) => ubah(sk.kunci, e.target.value)}
                                                    />
                                                    {sk.jenis === 'nombor' && <span className="rt-unit">{sk.unit === 'ms' ? 'seconds' : sk.unit === 'x' ? 'times' : sk.unit}</span>}
                                                </div>
                                            )}
                                            {b.ralat && <div className="rt-ralat">{b.ralat}</div>}
                                        </div>
                                        <div className="rt-aksi">
                                            {b.bukanLalai && (
                                                <button type="button" className="rt-btn rt-btn--ghost rt-btn--kecil" onClick={() => reset(sk)} title="Reset to default">
                                                    <RotateCcw size={13} /> Default
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                );
            })}

            <div className="rt-bar">
                <span className="rt-dim">
                    {bilRalat ? `${bilRalat} invalid` : bilDiubah ? `${bilDiubah} unsaved change(s)` : 'No unsaved changes'}
                </span>
                <div className="rt-bar-aksi">
                    <button type="button" className="rt-btn rt-btn--ghost" onClick={buangSemua} disabled={!bilDiubah || menyimpan}>Discard</button>
                    <button type="button" className="rt-btn rt-btn--utama" onClick={simpan} disabled={!bilDiubah || !!bilRalat || menyimpan}>
                        {menyimpan ? <Loader2 size={14} className="rt-pusing" /> : null} Save
                    </button>
                </div>
            </div>
        </div>
    );
}
